import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  getPulseActorOrder,
  getPulseErrorMessage,
  getPulsePayload,
  isPulseApiSuccess,
} from '../../services/pulseApi';
import {
  formatDeckPosition,
  formatNumber,
  formatPulseDate,
  formatWhyCard,
  isMatchRatingCard,
  parseActorOrderPayload,
  parseActorOrderSearch,
  statusChipColor,
} from './pulseUtils';

const PAGE_SIZE = 50;

function SummaryChip({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, minWidth: 100 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="subtitle1" fontWeight={700}>
        {value}
      </Typography>
    </Paper>
  );
}

function formatHitCardIds(hit) {
  const ids = hit?.card_ids || hit?.cardIds || hit?.cards || [];
  if (Array.isArray(ids)) {
    if (!ids.length) return '—';
    return ids
      .map((c) => (typeof c === 'string' ? c.slice(0, 8) : c?.id ? String(c.id).slice(0, 8) : '?'))
      .join(', ');
  }
  if (typeof ids === 'string') return ids;
  return '—';
}

function PulseActorOrder() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(parseActorOrderPayload(null));
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const applied = useMemo(
    () => ({
      key: searchParams.get('key') || '',
      user_id: searchParams.get('user_id') || '',
      installation_id: searchParams.get('installation_id') || '',
      guest_hash: searchParams.get('guest_hash') || '',
      offset: Math.max(0, Number(searchParams.get('offset') || 0) || 0),
    }),
    [searchParams]
  );

  const hasLookup =
    !!(applied.key || applied.user_id || applied.installation_id || applied.guest_hash);

  const displayKey =
    applied.key ||
    order.pulse_log_key ||
    (applied.user_id ? `pulse:${applied.user_id}` : '') ||
    (applied.installation_id ? `pulse:install:${applied.installation_id}` : '') ||
    '';

  const [lookupInput, setLookupInput] = useState(
    applied.key || applied.user_id || applied.installation_id || applied.guest_hash || ''
  );

  useEffect(() => {
    setLookupInput(
      applied.key || applied.user_id || applied.installation_id || applied.guest_hash || ''
    );
  }, [applied.key, applied.user_id, applied.installation_id, applied.guest_hash]);

  const writeLookup = useCallback(
    (raw, offset = 0) => {
      const parsed = parseActorOrderSearch(raw);
      const params = {};
      if (parsed.key) params.key = parsed.key;
      if (parsed.user_id) params.user_id = String(parsed.user_id);
      if (parsed.installation_id) params.installation_id = parsed.installation_id;
      if (parsed.guest_hash) params.guest_hash = parsed.guest_hash;
      if (offset) params.offset = String(offset);
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const loadOrder = useCallback(async () => {
    if (!hasLookup) {
      setOrder(parseActorOrderPayload(null));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await getPulseActorOrder({
        limit: PAGE_SIZE,
        offset: applied.offset,
        key: applied.key || undefined,
        user_id: applied.user_id || undefined,
        installation_id: applied.installation_id || undefined,
        guest_hash: applied.guest_hash || undefined,
      });
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to load actor Pulse order.'));
        setOrder(parseActorOrderPayload(null));
        return;
      }
      setOrder(parseActorOrderPayload(getPulsePayload(result)));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load actor Pulse order.');
      setOrder(parseActorOrderPayload(null));
    } finally {
      setLoading(false);
    }
  }, [applied, hasLookup]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const copyKey = async () => {
    if (!displayKey) return;
    try {
      await navigator.clipboard.writeText(displayKey);
      setSnackbar({ open: true, message: `Copied ${displayKey}` });
    } catch {
      setSnackbar({ open: true, message: 'Could not copy to clipboard' });
    }
  };

  const items = order.current_order?.items || [];
  const total = order.current_order?.total ?? items.length;
  const hasMore = applied.offset + items.length < total;
  const recentHits = order.recent_hits || [];

  const continuePin = order.continue_pin;
  const continueLabel =
    continuePin == null
      ? null
      : typeof continuePin === 'string'
        ? continuePin
        : continuePin.card_id || continuePin.id || JSON.stringify(continuePin);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton component={Link} to="/admin/pulse/actors" size="small" title="Back to users">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">Actor order (BE truth)</Typography>
        </Box>
        <IconButton onClick={loadOrder} disabled={loading || !hasLookup} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Live <code>GET /pulse/cards</code> order for this actor after seen / done / match continue.
        Compare to what support sees in-app: same order but app differs → <strong>FE</strong> (paging
        / splice / hot-take hide); wrong here → <strong>BE</strong>. Paste Cloudflare key when
        debugging with BE.
      </Alert>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Lookup"
          value={lookupInput}
          onChange={(e) => setLookupInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && writeLookup(lookupInput, 0)}
          placeholder="pulse:12345 · user id · installation id"
          sx={{ minWidth: 320 }}
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => writeLookup(lookupInput, 0)}
        >
          Load order
        </Button>
        {displayKey ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label={displayKey} sx={{ fontFamily: 'monospace' }} />
            <IconButton size="small" title="Copy Cloudflare search key" onClick={copyKey}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!hasLookup && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Enter a <code>pulse:…</code> key, user id, or installation id to load BE order.
        </Alert>
      )}

      {hasLookup && (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            <SummaryChip label="Seen" value={formatNumber(order.seen_count)} />
            <SummaryChip label="Done" value={formatNumber(order.done_count)} />
            <SummaryChip label="Cards in order" value={formatNumber(total)} />
            {order.why != null && order.why !== '' && (
              <Paper variant="outlined" sx={{ px: 1.5, py: 1, maxWidth: 420 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Why / pin
                </Typography>
                <Typography variant="body2">{formatWhyCard(order.why)}</Typography>
              </Paper>
            )}
            {continueLabel && (
              <Paper variant="outlined" sx={{ px: 1.5, py: 1, maxWidth: 320 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Continue pin
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {continueLabel}
                </Typography>
              </Paper>
            )}
          </Box>

          {loading && <LinearProgress sx={{ mb: 1 }} />}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Current order
          </Typography>
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Why</TableCell>
                  <TableCell>Expires</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No cards in current order for this actor.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((card, index) => {
                    const pos =
                      card.deck_position ?? card.deckPosition ?? applied.offset + index + 1;
                    const why = card.why ?? card.reason ?? card.match_continue_reason;
                    return (
                      <TableRow
                        key={card.id || `${pos}-${index}`}
                        hover
                        component={card.id ? Link : 'tr'}
                        to={
                          card.id
                            ? `/admin/pulse/cards/${encodeURIComponent(card.id)}`
                            : undefined
                        }
                        sx={{
                          cursor: card.id ? 'pointer' : 'default',
                          textDecoration: 'none',
                          color: 'inherit',
                        }}
                      >
                        <TableCell>{formatDeckPosition(pos)}</TableCell>
                        <TableCell>
                          {card.status ? (
                            <Chip
                              size="small"
                              label={card.status}
                              color={statusChipColor(card.status)}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {card.card_type || card.category || card.bucket || '—'}
                          </Typography>
                          {isMatchRatingCard(card) && (
                            <Chip size="small" label="Rate Player" sx={{ mt: 0.5 }} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {card.title || card.statement || 'Untitled'}
                          </Typography>
                          {card.id && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {String(card.id).slice(0, 8)}…
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatWhyCard(why)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatPulseDate(card.expires_at ?? card.expiresAt)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Button
              disabled={applied.offset <= 0 || loading}
              onClick={() => {
                const params = Object.fromEntries(searchParams.entries());
                params.offset = String(Math.max(0, applied.offset - PAGE_SIZE));
                if (params.offset === '0') delete params.offset;
                setSearchParams(params);
              }}
            >
              Previous
            </Button>
            <Typography variant="body2" color="text.secondary">
              {total === 0
                ? '0 cards'
                : `${applied.offset + 1}–${applied.offset + items.length} of ${total}`}
            </Typography>
            <Button
              disabled={!hasMore || loading}
              onClick={() => {
                const params = Object.fromEntries(searchParams.entries());
                params.offset = String(applied.offset + PAGE_SIZE);
                setSearchParams(params);
              }}
            >
              Next
            </Button>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Recent hits
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Last FE fetches from <code>pulse_request_log</code> (card_ids, next_card, match_continue).
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Card ids</TableCell>
                  <TableCell>Next</TableCell>
                  <TableCell>Match continue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentHits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No recent hits logged for this actor.
                    </TableCell>
                  </TableRow>
                ) : (
                  recentHits.map((hit, index) => (
                    <TableRow key={hit.id || `${hit.created_at}-${index}`}>
                      <TableCell>
                        {formatPulseDate(
                          hit.created_at || hit.createdAt || hit.at || hit.timestamp
                        )}
                      </TableCell>
                      <TableCell>{hit.action || hit.last_action || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, maxWidth: 280 }}>
                        {formatHitCardIds(hit)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {hit.next_card || hit.nextCard || hit.next_card_id || '—'}
                      </TableCell>
                      <TableCell>
                        {hit.match_continue != null
                          ? String(hit.match_continue)
                          : hit.matchContinue != null
                            ? String(hit.matchContinue)
                            : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}

export default PulseActorOrder;
